import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, from, map, merge, mergeMap, of, switchMap, take, tap } from 'rxjs';

import { AUTH_API } from '../apis/auth.api';
import { ProfileActions } from '../profile/profile.actions';
import { AuthActions } from './auth.actions';

function toErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err);
  } catch {
    return 'Unknown error';
  }
}

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authApi = inject(AUTH_API);
  private router = inject(Router);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginRequested),
      mergeMap(({ email, password }) =>
        from(this.authApi.login(email, password)).pipe(
          map(({ uid }) => AuthActions.loginSucceeded({ uid })),
          catchError((err) => of(AuthActions.loginFailed({ error: toErrorMessage(err) })))
        )
      )
    )
  );

  loadProfileOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSucceeded),
      map(({ uid }) => ProfileActions.loadProfileRequested({ uid }))
    )
  );

  /**
   * WICHTIG: Nicht direkt nach loginSucceeded navigieren.
   * Erst navigieren, wenn das Profil geladen ist -> sonst blocken Guards und du bleibst auf /login hängen.
   */
  navigateAfterLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSucceeded),
        switchMap(() =>
          merge(
            this.actions$.pipe(ofType(ProfileActions.loadProfileSucceeded), map(() => 'ok' as const)),
            this.actions$.pipe(ofType(ProfileActions.loadProfileFailed), map(() => 'fail' as const))
          ).pipe(
            take(1),
            tap((result) => {
              if (!this.router.url.startsWith('/login')) return;

              if (result === 'ok') {
                void this.router.navigateByUrl('/dashboard');
              } else {
                void this.router.navigateByUrl('/login');
              }
            })
          )
        )
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logoutRequested),
      mergeMap(() =>
        from(this.authApi.logout()).pipe(
          map(() => AuthActions.logoutSucceeded()),
          catchError((err) => of(AuthActions.logoutFailed({ error: toErrorMessage(err) })))
        )
      )
    )
  );

  clearProfileOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logoutSucceeded),
      map(() => ProfileActions.clearProfile())
    )
  );

  navigateAfterLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSucceeded),
        tap(() => {
          void this.router.navigateByUrl('/login');
        })
      ),
    { dispatch: false }
  );
}
